import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { filter, map, startWith } from 'rxjs';
import { capitalizeWords } from '../../../lib/capitalize';
import { getFullPath } from '../../../lib/get-full-path';
import { JsonLDService } from '../../../services/json-ld.service';
import { StoryblokHelperService } from '../../../services/storyblok-helper.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';

@Component({
  selector: 'app-article-breadcrumb',
  templateUrl: './article-breadcrumb.component.html',
  styleUrls: ['./article-breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, SvgComponent],
})
export class ArticleBreadcrumbComponent implements OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private storyblokHelperService = inject(StoryblokHelperService);
  private jsonLDService = inject(JsonLDService);

  story = input<ISbStoryData>();

  readonly mappedPath = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(undefined),
      map(() => {
        const path = getFullPath(this.route.snapshot);
        path.pop();
        return [
          {
            text: 'Home',
            link: '/',
          },
          ...path.map((segment, index) => {
            return {
              text: capitalizeWords(segment.split('-').join(' ')),
              link: '/' + path.slice(0, index + 1).join('/'),
            };
          }),
        ];
      })
    )
  );

  breadcrumb = computed<{ text: string; link: string; isCurrentPage?: boolean }[]>(() => {
    const story = this.story();
    if (!story) return [];
    const topicUuid = story.content.topic;
    const relationships = this.storyblokHelperService.getGlobalData('relationships');
    const topic = relationships.find(re => re.uuid === topicUuid);
    const mappedPath = this.mappedPath();
    if (!topic || !mappedPath) return mappedPath;

    const topicPage = relationships.find(re => re.uuid === topic.content.page);
    if (!topicPage) return mappedPath;

    return [
      ...mappedPath,
      {
        text: topic.name,
        link: `/${topicPage.full_slug}`,
      },
      {
        link: `/${story.full_slug}`,
        text: story.content.title,
        isCurrentPage: true,
      },
    ];
  });

  constructor() {
    effect(() => {
      const breadcrumb = this.breadcrumb();
      if (!breadcrumb) return;

      this.jsonLDService.add('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((item, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            item: { '@id': item.link, name: item.text },
          };
        }),
      });
    });
  }

  ngOnDestroy() {
    this.jsonLDService.remove('breadcrumb');
  }
}
